/**
 * Deferment Form Component
 *
 * Conditional form shown when outcome === 'deferment'.
 */

import Box from '@mui/material/Box';
import TextField from '@mui/material/TextField';
import Typography from '@mui/material/Typography';
import { useFormContext } from 'react-hook-form';

import type { FieldErrors } from 'react-hook-form';
import { sanitizeDateValue, getDateInputError, DATE_INPUT_MAX } from '@/shared/utils/dateInputHelpers';

// ============================================================================
// Types
// ============================================================================

interface DefermentFormProps {
  disabled?: boolean;
}

// Helper to get deferment field error
function getDefermentFieldError(
  errors: FieldErrors,
  field: 'reason' | 'promised_date'
): string | undefined {
  const defermentErrors = errors['deferment'];
  if (!defermentErrors || typeof defermentErrors !== 'object') return undefined;
  const fieldError = (defermentErrors as Record<string, { message?: string }>)[field];
  return fieldError?.message;
}

// ============================================================================
// Component
// ============================================================================

export function DefermentForm({ disabled = false }: DefermentFormProps) {
  const {
    register,
    formState: { errors },
    watch,
  } = useFormContext();

  const reasonError = getDefermentFieldError(errors, 'reason');
  const promisedDateError = getDefermentFieldError(errors, 'promised_date');
  const promisedDateReg = register('deferment.promised_date');
  const promisedDateValue = watch('deferment.promised_date') as string | undefined;
  const dateValidationError = promisedDateValue ? getDateInputError(promisedDateValue) : null;
  const displayDateError = promisedDateError || dateValidationError;

  return (
    <Box sx={{ mt: 2, mb: 2 }}>
      <Typography variant="subtitle2" color="text.secondary" sx={{ mb: 2 }}>
        Deferment Details
      </Typography>

      <Box
        sx={{
          display: 'flex',
          flexDirection: 'column',
          gap: 2,
          p: 2,
          bgcolor: 'action.hover',
          borderRadius: 1.5,
        }}
      >
        <TextField
          {...register('deferment.reason')}
          label="Reason for Deferment"
          fullWidth
          multiline
          rows={2}
          placeholder="Enter the reason for deferment..."
          error={!!reasonError}
          helperText={reasonError}
          disabled={disabled}
          sx={{
            '& .MuiOutlinedInput-root': {
              borderRadius: 1.5,
            },
          }}
        />

        <TextField
          name={promisedDateReg.name}
          ref={promisedDateReg.ref}
          onBlur={promisedDateReg.onBlur}
          onChange={(e) => {
            e.target.value = sanitizeDateValue(e.target.value);
            promisedDateReg.onChange(e);
          }}
          type="date"
          label="Promised Payment Date"
          fullWidth
          InputLabelProps={{ shrink: true }}
          error={!!displayDateError}
          helperText={displayDateError}
          disabled={disabled}
          inputProps={{ max: DATE_INPUT_MAX }}
          sx={{
            '& .MuiOutlinedInput-root': {
              borderRadius: 1.5,
            },
          }}
        />
      </Box>
    </Box>
  );
}
